import { useBreakpointValue, useTheme } from "@chakra-ui/react";
import {
  Bar,
  BarChart,
  Cell,
  LabelList,
  Legend,
  LegendType,
  ResponsiveContainer,
  XAxis,
  YAxis,
  Text
} from "recharts";
import truncate from "lodash/truncate";
import uniqBy from "lodash/uniqBy";
import { Tooltip as ChakraUITooltip } from "@chakra-ui/react";
import { omit } from "lodash";

interface HorizontalBarChartProps {
  data: { name: string; value: number; highlight?: boolean; legend: string }[];
}

function HorizontalBarChart(props: HorizontalBarChartProps) {
  const { data } = props;
  const { fonts, colors, fontSizes } = useTheme();
  const width = useBreakpointValue({ base: 348, lg: 348, md: 183, sm: 136 });
  const truncateLength = useBreakpointValue({
    base: 122,
    "2xl": 122,
    xl: 122,
    lg: 122,
    md: 38,
    sm: 38
  }) as number;

  const legendPayload: Array<{
    value: string;
    type: LegendType;
    color?: string;
  }> = uniqBy(
    data
      .sort((a, b) => (b.highlight as never) - (a.highlight as never))
      .map(({ legend, highlight }) => {
        if (highlight) {
          return { value: legend, type: "square", color: colors?.brand[500] };
        }

        return { value: legend, type: "square" };
      }),
    "value"
  );

  return (
    <ResponsiveContainer width="100%" height={data.length * 55}>
      <BarChart
        data={data}
        layout="vertical"
        barCategoryGap={1}
        barGap={28}
        barSize={20}
        margin={{ top: 0, right: 50, left: 0, bottom: 0 }}
      >
        <XAxis type="number" hide />
        <YAxis
          type="category"
          width={width}
          dataKey="name"
          tickLine={false}
          fontFamily={fonts?.body}
          stroke={colors?.gray[800]}
          fontSize={fontSizes["xs"]}
          axisLine={{ stroke: colors?.gray[300] }}
          tick={(tick) => (
            <ChakraUITooltip
              label={tick.payload.value}
              aria-label={tick.payload.value}
              isDisabled={tick.payload.value.length < truncateLength}
            >
              <g>
                <Text
                  {...omit(tick, [
                    "payload",
                    "tickFormatter",
                    "visibleTicksCount"
                  ])}
                >
                  {truncate(tick.payload.value, { length: truncateLength })}
                </Text>
              </g>
            </ChakraUITooltip>
          )}
        />

        <Bar dataKey="value" radius={[0, 4, 4, 0]} fontFamily={fonts?.body}>
          {data.map((entry, index) => (
            <Cell
              key={`cell-${index}`}
              fontSize={fontSizes["xs"]}
              fill={entry.highlight ? colors.brand[500] : colors.gray[400]}
            />
          ))}
          <LabelList
            dataKey="value"
            position="right"
            fill={colors?.gray[800]}
          />
        </Bar>
        <Legend
          verticalAlign="top"
          fill={colors.gray[400]}
          payload={legendPayload ?? []}
          wrapperStyle={{
            fontSize: fontSizes["xs"],
            fill: colors?.gray[400]
          }}
        />
      </BarChart>
    </ResponsiveContainer>
  );
}

export { HorizontalBarChart };
export type { HorizontalBarChartProps };
